/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-floating-promises */
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Button } from "@mui/material";
import { usePopup } from "@nodeon/react-components";
import { addMinutes } from "date-fns";
import { useSnackbar } from "notistack";
import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  BerthOrderCreateDto,
  BerthOrderDetailsDto,
  BerthOrderEditDto,
  useBerthApi,
} from "~/api/GeneratedApi";
import GenericStepperDialog, {
  DialogStep,
} from "~/components/GenericStepperDialog";
import LoadingBox from "~/components/LoadingBox";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { BerthOrderForm, BerthOrderFormInputs } from "./BerthOrderForm";

export type BerthDialogAction = "add" | "edit" | "view";

type Props = {
  activeAction?: BerthDialogAction;
  berthOrderId?: number;
  open?: boolean;
  berthId?: number;
  berthName?: string;
  startTimestamp?: Date;
  endTimestamp?: Date;
  mmsi?: number;
  portId?: number;
  onSubmit?: () => void;
  onClose: () => void;
};

function formDataToArgs(
  x: BerthOrderFormInputs,
): BerthOrderCreateDto | BerthOrderEditDto {
  return {
    ...x,
    berthId: x?.berthId ?? -1,
    startTimestamp: x?.startTimestamp ?? new Date(),
    endTimestamp: x?.endTimestamp ?? new Date(),
    cordMen: x?.cordMen ?? false,
    water: x?.water ?? false,
    power: x?.power ?? false,
    garbage: x?.garbage ?? false,
    vesselId: x?.vesselId ?? -1,
  };
}

export const BerthOrderDialog = ({
  activeAction,
  onClose,
  berthOrderId,
  berthId,
  startTimestamp,
  endTimestamp,
  onSubmit,
  mmsi,
  portId,
  berthName,
  open = true,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("berths");
  const actions: BerthDialogAction[] = ["add", "edit", "view"];
  const formId = `${activeAction}-berth-order-form`;
  const nextRef = useRef<() => void>();
  const { confirm } = usePopup();
  const { enqueueSnackbar } = useSnackbar();
  const berthApi = useBerthApi();

  const isEditing = activeAction === "edit";
  const isCreating = activeAction === "add";
  const isViewing = activeAction === "view";

  const { data: berthOrder, isLoading: isLoadingBerthOrder } = useCustomQuery(
    { key: "berth-order", id: berthOrderId },
    () => berthApi.getBerthOrder(berthOrderId!, "-1"), // The second value here is not even used in backend
    { enabled: !!berthOrderId },
  );

  const { mutate: addMutation } = useCustomMutation(
    ({ order, id }: { order: BerthOrderCreateDto; id: number }) =>
      berthApi.addBerthOrder(order, id),
    {
      onSuccess: () => {
        onClose();
        onSubmit?.();
      },
    },
  );

  const { mutate: editMutation } = useCustomMutation(
    ({ order, id }: { order: BerthOrderEditDto; id: number }) =>
      berthApi.editBerthOrder(order, berthOrderId!, id),
    {
      onSuccess: () => {
        onClose();
        onSubmit?.();
      },
    },
  );

  const { mutate: deleteMutation } = useCustomMutation(
    () => berthApi.deleteBerthOrder(berthOrderId!, "-1"),
    {
      onSuccess: () => {
        onClose();
        onSubmit?.();
      },
      onError: () => {
        enqueueSnackbar(t("failed-to-delete"));
      },
    },
  );

  const steps: DialogStep[] = useMemo(() => {
    if (isLoadingBerthOrder) {
      return [
        {
          title: t("general:loading"),
          content: <LoadingBox />,
        },
      ];
    }

    let title = "";
    switch (activeAction) {
      case "add":
        title = t("fill-order-info");
        break;
      case "edit":
        title = t("edit-order-info");
        break;
      case "view":
        title = t("view-berth-order");
        break;
      default:
        break;
    }
    let end = endTimestamp;
    if (!end) end = startTimestamp ? addMinutes(startTimestamp, 30) : undefined;

    return [
      {
        title,
        content: (
          <BerthOrderForm
            onSubmit={(d) => {
              if (!d.berthId) return;
              if (isCreating) {
                addMutation({ order: formDataToArgs(d), id: d.berthId });
              } else if (isEditing) {
                editMutation({
                  order: formDataToArgs(d) as BerthOrderEditDto,
                  id: d.berthId,
                });
              }
            }}
            initialBerthId={berthId ?? null}
            berthName={berthName}
            portId={portId}
            formId={formId}
            defaultValues={
              isCreating
                ? {
                    startTimestamp,
                    endTimestamp: end,
                    berthId,
                    vesselId: mmsi,
                  }
                : (berthOrder as BerthOrderDetailsDto)
            }
            readOnly={isViewing}
          />
        ),
      },
    ];
  }, [
    isLoadingBerthOrder,
    activeAction,
    endTimestamp,
    startTimestamp,
    berthId,
    berthName,
    portId,
    formId,
    isCreating,
    mmsi,
    berthOrder,
    isViewing,
    t,
    isEditing,
    addMutation,
    editMutation,
  ]);

  const renderNextButton = useCallback(
    (step) => {
      if (isViewing) return null;

      switch (step) {
        case 0:
          return (
            <Button
              type="submit"
              form={formId}
              variant="contained"
              color="secondary"
            >
              {isCreating ? t("general:add") : t("general:edit")}
            </Button>
          );
        default:
          return null;
      }
    },
    [isViewing, formId, isCreating, t],
  );

  const renderBackButton = useCallback(
    (_step, _back, handleDialogClose) => {
      return (
        <Button
          onClick={() => {
            handleDialogClose();
            onClose();
          }}
        >
          {isViewing ? t("general:close") : t("general:cancel")}
        </Button>
      );
    },
    [isViewing, onClose, t],
  );

  if (!activeAction || !actions.includes(activeAction)) return null;

  return (
    <GenericStepperDialog
      open={open}
      steps={steps}
      onClose={onClose}
      dialogTitle=""
      minHeight="40%"
      height="50%"
      maxWidth="md"
      extraDialogActions={
        isEditing && (
          <Button
            style={{ marginRight: "auto" }}
            variant="outlined"
            color="primary"
            startIcon={<DeleteIcon />}
            onClick={() => {
              confirm({
                title: t("general:are-you-sure"),
                description: t("are-you-sure-delete"),
              }).then(() => deleteMutation());
            }}
          >
            {t("general:delete")}
          </Button>
        )
      }
      renderNextButton={renderNextButton}
      renderBackButton={renderBackButton}
      nextRef={nextRef}
      hideSteps
    />
  );
};
